import { DiscoPublisherStatus } from "@redotech/congo-enums/disco-publisher-status";
import { OfferNetworkProvider } from "@redotech/crdb-enums/marketing/offer-network-provider";
import { OfferSurface } from "@redotech/crdb-enums/marketing/offer-surface";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const prettyOfferNetworkProvider = {
  [OfferNetworkProvider.Rokt]: "Rokt",
  // Falcon is white-labeled as Redo's own offer network in merchant-facing UI.
  [OfferNetworkProvider.Falcon]: "Redo Offer Network",
  [OfferNetworkProvider.Uptick]: "Uptick",
  [OfferNetworkProvider.Disco]: "Disco",
};

/**
 * Providers a merchant can place and turn off. Uptick takes no credentials —
 * enrolling is the integration document existing — but it is still placed and
 * disconnected like the others.
 */
export const SELECTABLE_OFFER_PROVIDERS = [
  OfferNetworkProvider.Rokt,
  OfferNetworkProvider.Falcon,
  OfferNetworkProvider.Uptick,
  OfferNetworkProvider.Disco,
] as const;

export type SelectableOfferProvider =
  (typeof SELECTABLE_OFFER_PROVIDERS)[number];

/**
 * A merchant's chosen offer placement, shared by the tracking-page OFFERS block
 * and the Shopify thank-you setting. Empty id = unconfigured.
 */
export const offerPlacementSelectionSchema = z.object({
  provider: createEnum(Object.values(OfferNetworkProvider)),
  offerPlacementId: z.string().default(""),
});
export interface OfferPlacementSelection extends z.infer<
  typeof offerPlacementSelectionSchema
> {}

/** Merchant attestation that shopper data is shared with the offer network. */
export const offerNetworkDataSharingConsentSchema = z.object({
  acknowledgedAt: z.date(),
  userEmail: z.string().optional(),
});
export interface OfferNetworkDataSharingConsent extends z.infer<
  typeof offerNetworkDataSharingConsentSchema
> {}

/** Where an offer placement is displayed. Placements are one-per-surface. */
export enum OfferPlacementSurface {
  TRACKING_PAGE = "tracking-page",
  RETURNS_CONFIRMATION = "returns-confirmation",
  HOSTED_PAGE = "hosted-page",
  SHOPIFY_THANK_YOU = "shopify-thank-you",
  SHOPIFY_ORDER_STATUS = "shopify-order-status",
}

export const prettyOfferPlacementSurface = {
  [OfferPlacementSurface.TRACKING_PAGE]: "Tracking page",
  [OfferPlacementSurface.RETURNS_CONFIRMATION]: "Returns confirmation",
  [OfferPlacementSurface.HOSTED_PAGE]: "Hosted page",
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: "Shopify thank-you page",
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]: "Shopify order status page",
};

const WEB_SDK_OFFER_PROVIDERS = [
  OfferNetworkProvider.Rokt,
  OfferNetworkProvider.Falcon,
] as const;

/**
 * Providers usable on each surface — source of truth for the dropdown and
 * backend validation. Rokt can't run in a Shopify checkout UI extension yet.
 *
 * Disco serves the tracking page and both Shopify surfaces.
 *
 * Uptick is offered only on its SHOPIFY_EXTENSION-routed surfaces. Its web
 * channel is built and routed, but not enabled: offers.js appends an unscoped
 * form reset to the host page, which on a tracking page is the merchant's own
 * UI. Re-enable by adding Uptick back to TRACKING_PAGE here once Uptick scopes
 * that stylesheet; uptick-routing.spec pins the two in step.
 */
export const supportedOfferProvidersBySurface: Record<
  OfferPlacementSurface,
  readonly OfferNetworkProvider[]
> = {
  [OfferPlacementSurface.TRACKING_PAGE]: [
    ...WEB_SDK_OFFER_PROVIDERS,
    OfferNetworkProvider.Disco,
  ],
  [OfferPlacementSurface.RETURNS_CONFIRMATION]: WEB_SDK_OFFER_PROVIDERS,
  [OfferPlacementSurface.HOSTED_PAGE]: WEB_SDK_OFFER_PROVIDERS,
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: [
    OfferNetworkProvider.Falcon,
    OfferNetworkProvider.Uptick,
    OfferNetworkProvider.Disco,
  ],
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]: [
    OfferNetworkProvider.Falcon,
    OfferNetworkProvider.Uptick,
    OfferNetworkProvider.Disco,
  ],
};

/**
 * The domain a merchant's storefront is known by, for offer networks that key
 * reporting on a site. Prefers the canonical/custom domain over *.myshopify.com
 * because that is what a shopper's browser reports, and returns a bare host so
 * two callers cannot disagree over a scheme or trailing slash.
 */
export function offerNetworkSiteDomain(team: {
  storeUrl?: string;
  domain?: string | null;
}): string | undefined {
  const normalize = (value: string | null | undefined): string | undefined =>
    value
      ?.trim()
      .replace(/^https?:\/\//i, "")
      .replace(/\/+$/, "") || undefined;
  return normalize(team.domain) ?? normalize(team.storeUrl);
}

export const isOfferProviderSupportedOnSurface = (
  surface: OfferPlacementSurface,
  provider: OfferNetworkProvider,
): boolean => supportedOfferProvidersBySurface[surface].includes(provider);

/**
 * Redo's two Uptick "places", one per delivery channel. Both appear in request
 * paths the browser makes, so they are public identifiers rather than secrets.
 */
export const UPTICK_SHOPIFY_PLACE_ID = "41d70fdd-4224-4a2c-b5b4-15eb1551806d";
export const UPTICK_WEB_PLACE_ID = "92176870-254c-40de-892b-cb7c56e32b68";

/** How Uptick classifies a page. Does not map 1:1 onto our surfaces. */
export enum UptickPlacementType {
  ORDER_CONFIRMATION = "order_confirmation",
  ORDER_STATUS = "order_status",
  /**
   * Partner-hosted page; tells Uptick to serve tracking-page offers. Uptick
   * gave us this value directly for order tracking. Their published web guide
   * lists only checkout/order_confirmation/order_status, so do not "fix" this
   * against the docs — the docs are the incomplete side.
   */
  EXTERNAL_ORDER_CONFIRMATION = "external_order_confirmation",
}

/** Which SDK serves a surface. The two are not interchangeable. */
export enum UptickChannel {
  /** Vendored SDK inside a Shopify UI extension. */
  SHOPIFY_EXTENSION = "shopify-extension",
  /** offers.js loaded into a Redo-hosted page. */
  WEB = "web",
}

export interface UptickSurfaceRouting {
  channel: UptickChannel;
  placeId: string;
  placement: UptickPlacementType;
}

/**
 * Exhaustive: a new surface must declare its Uptick routing or opt out with
 * null. Null means Uptick has given us no placement type for that surface.
 */
export const uptickRoutingByPlacementSurface: Record<
  OfferPlacementSurface,
  UptickSurfaceRouting | null
> = {
  [OfferPlacementSurface.TRACKING_PAGE]: {
    channel: UptickChannel.WEB,
    placeId: UPTICK_WEB_PLACE_ID,
    placement: UptickPlacementType.EXTERNAL_ORDER_CONFIRMATION,
  },
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: {
    channel: UptickChannel.SHOPIFY_EXTENSION,
    placeId: UPTICK_SHOPIFY_PLACE_ID,
    placement: UptickPlacementType.ORDER_CONFIRMATION,
  },
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]: {
    channel: UptickChannel.SHOPIFY_EXTENSION,
    placeId: UPTICK_SHOPIFY_PLACE_ID,
    placement: UptickPlacementType.ORDER_STATUS,
  },
  [OfferPlacementSurface.RETURNS_CONFIRMATION]: null,
  [OfferPlacementSurface.HOSTED_PAGE]: null,
};

/** Exhaustive: a new placement surface must be given a reporting surface here. */
export const offerSurfaceByPlacementSurface: Record<
  OfferPlacementSurface,
  OfferSurface
> = {
  [OfferPlacementSurface.TRACKING_PAGE]: OfferSurface.TrackingPage,
  [OfferPlacementSurface.RETURNS_CONFIRMATION]:
    OfferSurface.ReturnsConfirmation,
  [OfferPlacementSurface.HOSTED_PAGE]: OfferSurface.HostedPage,
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: OfferSurface.ThankYou,
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]: OfferSurface.OrderStatus,
};

/** Values are Falcon's exact accepted API enum (docs.falconlabs.com). */
export enum FalconAgeDemographic {
  UNDER_18 = "under_18",
  AGE_18_24 = "age_18_to_24",
  AGE_25_34 = "age_25_to_34",
  AGE_35_44 = "age_35_to_44",
  AGE_45_54 = "age_45_to_54",
  AGE_55_64 = "age_55_to_64",
  OVER_65 = "over_65",
  MIXED = "mixed",
}

export const prettyFalconAgeDemographic = {
  [FalconAgeDemographic.UNDER_18]: "Under 18",
  [FalconAgeDemographic.AGE_18_24]: "18–24",
  [FalconAgeDemographic.AGE_25_34]: "25–34",
  [FalconAgeDemographic.AGE_35_44]: "35–44",
  [FalconAgeDemographic.AGE_45_54]: "45–54",
  [FalconAgeDemographic.AGE_55_64]: "55–64",
  [FalconAgeDemographic.OVER_65]: "65+",
  [FalconAgeDemographic.MIXED]: "Mixed / all ages",
};

export enum FalconGenderDemographic {
  MALE = "male",
  FEMALE = "female",
  MIXED = "mixed",
  OTHER = "other",
}

export const prettyFalconGenderDemographic = {
  [FalconGenderDemographic.MALE]: "Mostly male",
  [FalconGenderDemographic.FEMALE]: "Mostly female",
  [FalconGenderDemographic.MIXED]: "Mixed",
  [FalconGenderDemographic.OTHER]: "Other",
};

const falconSiteSettingsSchema_ = z.object({
  // `.catch` keeps a stored value Falcon's enum no longer recognizes from
  // failing the whole integration parse (which would drop its placements).
  ageDemographics: createEnum(Object.values(FalconAgeDemographic))
    .optional()
    .catch(undefined),
  genderDemographics: createEnum(Object.values(FalconGenderDemographic))
    .optional()
    .catch(undefined),
  categoryName: z.string().optional(),
});
export type FalconSiteSettings = z.infer<typeof falconSiteSettingsSchema_>;
export const falconSiteSettingsSchema: z.ZodType<FalconSiteSettings> =
  falconSiteSettingsSchema_;

const roktOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Page identifier issued by the merchant's Rokt contact. */
  pageIdentifier: z.string(),
  sandbox: z.boolean(),
});
export const roktOfferPlacementSchema: z.ZodType<RoktOfferPlacement> =
  roktOfferPlacementSchema_;
export interface RoktOfferPlacement extends z.infer<
  typeof roktOfferPlacementSchema_
> {}

const falconOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Falcon's vendor placement id, returned by their placements API. */
  falconPlacementId: z.string(),
  /** false = Falcon serves mock/test offers (no revenue or attribution). */
  isLiveMode: z.boolean(),
});
export const falconOfferPlacementSchema: z.ZodType<FalconOfferPlacement> =
  falconOfferPlacementSchema_;
export interface FalconOfferPlacement extends z.infer<
  typeof falconOfferPlacementSchema_
> {}

const uptickOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Minted by us; sent as `app_id` and reported on by Uptick. Immutable. */
  appExternalId: z.string(),
});
export const uptickOfferPlacementSchema: z.ZodType<UptickOfferPlacement> =
  uptickOfferPlacementSchema_;
export interface UptickOfferPlacement extends z.infer<
  typeof uptickOfferPlacementSchema_
> {}

/** Disco widget-config formats: tracking drawer, tracking inline, Shopify thank-you/order status. */
export enum DiscoWidgetFormat {
  ESSENTIAL_PULLUP = "ESSENTIAL_PULLUP",
  ESSENTIAL_INLINE = "ESSENTIAL_INLINE",
  SHOPIFY_NATIVE_ESSENTIAL = "SHOPIFY_NATIVE_ESSENTIAL",
}

const discoOfferPlacementSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  surface: createEnum(Object.values(OfferPlacementSurface)),
  /** Disco-issued widget id; never the Redo placement id. */
  widgetId: z.string(),
  widgetFormat: createEnum(Object.values(DiscoWidgetFormat)),
});
export const discoOfferPlacementSchema: z.ZodType<DiscoOfferPlacement> =
  discoOfferPlacementSchema_;
export interface DiscoOfferPlacement extends z.infer<
  typeof discoOfferPlacementSchema_
> {}

/** Disco widget-config page types, one per surface Disco serves. */
export enum DiscoPageType {
  ORDER_TRACKING = "ORDER_TRACKING",
  THANK_YOU = "THANK_YOU",
  ORDER_STATUS = "ORDER_STATUS",
}

export const discoPageTypeBySurface: Partial<
  Record<OfferPlacementSurface, DiscoPageType>
> = {
  [OfferPlacementSurface.TRACKING_PAGE]: DiscoPageType.ORDER_TRACKING,
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]: DiscoPageType.THANK_YOU,
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]: DiscoPageType.ORDER_STATUS,
};

export type DiscoReviewState =
  | "notConnected"
  | "pending"
  | "approved"
  | "rejected";

const discoReviewStateByStatus: Record<DiscoPublisherStatus, DiscoReviewState> =
  {
    [DiscoPublisherStatus.Pending]: "pending",
    // Disco still reviews the store while it waits for more details.
    [DiscoPublisherStatus.NeedsInfo]: "pending",
    [DiscoPublisherStatus.Approved]: "approved",
    [DiscoPublisherStatus.Rejected]: "rejected",
  };

/** How a Disco publisher status reads in the UI; no status means not connected. */
export function discoReviewState(
  status: DiscoPublisherStatus | undefined,
): DiscoReviewState {
  return status === undefined
    ? "notConnected"
    : discoReviewStateByStatus[status];
}

export const discoWidgetFormatBySurface: Partial<
  Record<OfferPlacementSurface, DiscoWidgetFormat>
> = {
  [OfferPlacementSurface.TRACKING_PAGE]: DiscoWidgetFormat.ESSENTIAL_PULLUP,
  [OfferPlacementSurface.SHOPIFY_THANK_YOU]:
    DiscoWidgetFormat.SHOPIFY_NATIVE_ESSENTIAL,
  [OfferPlacementSurface.SHOPIFY_ORDER_STATUS]:
    DiscoWidgetFormat.SHOPIFY_NATIVE_ESSENTIAL,
};
